import type { LucideIcon } from 'lucide-react';
import {
  Gift,
  Gem,
  HeartPulse,
  Brain,
  PartyPopper,
  Users,
} from 'lucide-react';
import type { Language } from '@/i18n/types';

export interface GiftCategory {
  slug: string;
  Icon: LucideIcon;
}

export interface GiftCategoryLocalized extends GiftCategory {
  title: string;
  description: string;
  explore: string;
}

export const GIFT_CATEGORIES: GiftCategory[] = [
  { slug: 'express-tangibly', Icon: Gift },
  { slug: 'meaningful-keepsakes', Icon: Gem },
  { slug: 'whole-self-health', Icon: HeartPulse },
  { slug: 'healing-clarity', Icon: Brain },
  { slug: 'celebrate-appreciate', Icon: PartyPopper },
  { slug: 'shared-experiences', Icon: Users },
];

const categoryI18n: Record<string, Record<string, { title: string; description: string; explore: string }>> = {
  'express-tangibly': {
    en: { title: 'Express It Tangibly', description: 'Sometimes words want to be held. Send something they can feel.', explore: 'Explore →' },
    es: { title: 'Exprésalo Tangiblemente', description: 'A veces las palabras quieren ser sostenidas. Envía algo que puedan sentir.', explore: 'Explorar →' },
    fr: { title: 'Exprimez-le Concrètement', description: 'Parfois les mots veulent être tenus. Envoyez quelque chose de tangible.', explore: 'Explorer →' },
    pt: { title: 'Expresse de Forma Tangível', description: 'Às vezes as palavras querem ser tocadas. Envie algo que possam sentir.', explore: 'Explorar →' },
    ar: { title: 'عبّر بشكل ملموس', description: 'أحياناً الكلمات تريد أن تُمسك. أرسل شيئاً يمكنهم الشعور به.', explore: '← استكشف' },
    ja: { title: '形にして伝える', description: '言葉は時に手に取れるものを求めます。感じられるものを贈りましょう。', explore: '探索 →' },
    ko: { title: '실체로 표현하기', description: '때로는 말이 손에 잡히는 것을 원합니다. 느낄 수 있는 것을 보내세요.', explore: '탐색 →' },
    id: { title: 'Ungkapkan Secara Nyata', description: 'Terkadang kata-kata ingin dipegang. Kirim sesuatu yang bisa mereka rasakan.', explore: 'Jelajahi →' },
  },
  'meaningful-keepsakes': {
    en: { title: 'Meaningful Keepsakes', description: 'A symbol of what you meant — and still mean.', explore: 'Explore →' },
    es: { title: 'Recuerdos Significativos', description: 'Un símbolo de lo que significaste — y aún significas.', explore: 'Explorar →' },
    fr: { title: 'Souvenirs Significatifs', description: 'Un symbole de ce que vous vouliez dire — et dites encore.', explore: 'Explorer →' },
    pt: { title: 'Lembranças Significativas', description: 'Um símbolo do que você significou — e ainda significa.', explore: 'Explorar →' },
    ar: { title: 'تذكارات ذات معنى', description: 'رمز لما كنت تعنيه — ولا تزال.', explore: '← استكشف' },
    ja: { title: '意味のある記念品', description: 'あなたの思いの象徴 — 今もなお。', explore: '探索 →' },
    ko: { title: '의미있는 기념품', description: '당신이 의미했던 — 그리고 여전히 의미하는 것의 상징.', explore: '탐색 →' },
    id: { title: 'Kenangan Bermakna', description: 'Simbol dari apa yang Anda maksud — dan masih Anda maksud.', explore: 'Jelajahi →' },
  },
  'whole-self-health': {
    en: { title: 'Whole Self Health', description: 'When you feel better, you connect better. Health is emotional stability.', explore: 'Explore →' },
    es: { title: 'Salud Integral', description: 'Cuando te sientes mejor, conectas mejor. La salud es estabilidad emocional.', explore: 'Explorar →' },
    fr: { title: 'Santé Globale', description: 'Quand vous vous sentez mieux, vous connectez mieux. La santé est stabilité émotionnelle.', explore: 'Explorer →' },
    pt: { title: 'Saúde Integral', description: 'Quando você se sente melhor, conecta melhor. Saúde é estabilidade emocional.', explore: 'Explorar →' },
    ar: { title: 'صحة شاملة', description: 'عندما تشعر بتحسن، تتواصل بشكل أفضل. الصحة هي الاستقرار العاطفي.', explore: '← استكشف' },
    ja: { title: '全体的な健康', description: '体調が良いと、つながりも良くなります。健康は感情の安定です。', explore: '探索 →' },
    ko: { title: '전인 건강', description: '기분이 좋으면 연결도 좋아집니다. 건강은 감정적 안정입니다.', explore: '탐색 →' },
    id: { title: 'Kesehatan Menyeluruh', description: 'Saat Anda merasa lebih baik, Anda terhubung lebih baik. Kesehatan adalah stabilitas emosional.', explore: 'Jelajahi →' },
  },
  'healing-clarity': {
    en: { title: 'Healing & Clarity', description: 'Not every message is ready yet. Some need understanding first.', explore: 'Explore →' },
    es: { title: 'Sanación y Claridad', description: 'No todos los mensajes están listos. Algunos necesitan comprensión primero.', explore: 'Explorar →' },
    fr: { title: 'Guérison et Clarté', description: "Tous les messages ne sont pas prêts. Certains ont d'abord besoin de compréhension.", explore: 'Explorer →' },
    pt: { title: 'Cura e Clareza', description: 'Nem toda mensagem está pronta. Algumas precisam de compreensão primeiro.', explore: 'Explorar →' },
    ar: { title: 'شفاء ووضوح', description: 'ليست كل رسالة جاهزة بعد. بعضها يحتاج للفهم أولاً.', explore: '← استكشف' },
    ja: { title: '癒しと明晰さ', description: 'すべてのメッセージがまだ準備できているわけではありません。まず理解が必要なものもあります。', explore: '探索 →' },
    ko: { title: '치유와 명확함', description: '모든 메시지가 준비된 것은 아닙니다. 먼저 이해가 필요한 것도 있습니다.', explore: '탐색 →' },
    id: { title: 'Penyembuhan & Kejelasan', description: 'Tidak setiap pesan siap. Beberapa membutuhkan pemahaman terlebih dahulu.', explore: 'Jelajahi →' },
  },
  'celebrate-appreciate': {
    en: { title: 'Celebrate & Appreciate', description: 'Gratitude hits different when it arrives unexpectedly.', explore: 'Explore →' },
    es: { title: 'Celebrar y Agradecer', description: 'La gratitud se siente diferente cuando llega inesperadamente.', explore: 'Explorar →' },
    fr: { title: 'Célébrer et Apprécier', description: "La gratitude frappe différemment quand elle arrive à l'improviste.", explore: 'Explorer →' },
    pt: { title: 'Celebrar e Agradecer', description: 'A gratidão é diferente quando chega inesperadamente.', explore: 'Explorar →' },
    ar: { title: 'احتفل وقدّر', description: 'الامتنان يكون مختلفاً عندما يأتي بشكل غير متوقع.', explore: '← استكشف' },
    ja: { title: '祝福と感謝', description: '感謝は予期せぬ時に届くと、違った響きがあります。', explore: '探索 →' },
    ko: { title: '축하와 감사', description: '감사는 예상치 못할 때 다르게 다가옵니다.', explore: '탐색 →' },
    id: { title: 'Rayakan & Hargai', description: 'Rasa syukur terasa berbeda saat datang tanpa terduga.', explore: 'Jelajahi →' },
  },
  'shared-experiences': {
    en: { title: 'Shared Experiences', description: "The best gift isn't the object — it's time together.", explore: 'Explore →' },
    es: { title: 'Experiencias Compartidas', description: 'El mejor regalo no es el objeto — es tiempo juntos.', explore: 'Explorar →' },
    fr: { title: 'Expériences Partagées', description: "Le meilleur cadeau n'est pas l'objet — c'est le temps passé ensemble.", explore: 'Explorer →' },
    pt: { title: 'Experiências Compartilhadas', description: 'O melhor presente não é o objeto — é tempo juntos.', explore: 'Explorar →' },
    ar: { title: 'تجارب مشتركة', description: 'أفضل هدية ليست الشيء — بل الوقت معاً.', explore: '← استكشف' },
    ja: { title: '共有体験', description: '最高の贈り物はモノではなく、一緒に過ごす時間です。', explore: '探索 →' },
    ko: { title: '공유 경험', description: '최고의 선물은 물건이 아니라 함께하는 시간입니다.', explore: '탐색 →' },
    id: { title: 'Pengalaman Bersama', description: 'Hadiah terbaik bukan bendanya — tapi waktu bersama.', explore: 'Jelajahi →' },
  },
};

export function getLocalizedCategory(slug: string, lang: Language): GiftCategoryLocalized {
  const cat = GIFT_CATEGORIES.find(c => c.slug === slug)!;
  const i18n = categoryI18n[slug]?.[lang] || categoryI18n[slug]?.en || { title: slug, description: '', explore: 'Explore →' };
  return { ...cat, ...i18n };
}

export function getLocalizedCategories(lang: Language): GiftCategoryLocalized[] {
  return GIFT_CATEGORIES.map(c => getLocalizedCategory(c.slug, lang));
}
